import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FieldScheduleEntry,
  ENTRY_TYPE_LABELS,
  ENTRY_TYPE_COLORS,
  formatTime
} from '../../types/schedule';
import fieldNewsService, { FieldNewsDto } from '../../services/fieldNewsService';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: var(--legacy-card-bg, #0d1b2a);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  width: 100%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div<{ $color: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  background: ${props => props.$color}15;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #fff;
  font-family: 'Orbitron', sans-serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #8bb8d8;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #fff;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const TypeBadge = styled.span<{ $color: string }>`
  display: inline-block;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 4px;
  background: ${props => props.$color}30;
  color: ${props => props.$color};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  margin-bottom: 16px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: #8bb8d8;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoValue = styled.div`
  font-size: 15px;
  color: #fff;
  line-height: 1.5;
`;

const TimeDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  color: #00d4ff;
  font-family: 'Orbitron', monospace;
  font-weight: 600;
`;

const RefereeList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RefereeBadge = styled(Link)`
  display: inline-block;
  padding: 6px 12px;
  background: rgba(0, 212, 255, 0.15);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #fff;
  font-size: 13px;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.3);
    border-color: #00d4ff;
    color: #00d4ff;
  }
`;

const Description = styled.div`
  font-size: 14px;
  color: #ccc;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  border-top: 1px solid rgba(0, 212, 255, 0.2);
`;

const CloseBtn = styled.button`
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  color: #00d4ff;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-color: #00d4ff;
  }
`;

const NewsLinkButton = styled.button`
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(135deg, #4CAF50, #2E7D32);
  border: none;
  color: white;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
  }
`;

const NewsSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 8px;
`;

const NewsSectionTitle = styled.div`
  font-size: 12px;
  color: #4CAF50;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

const NewsTitle = styled.div`
  font-size: 15px;
  color: #fff;
  font-weight: 600;
  margin-bottom: 8px;
`;

interface ScheduleEntryViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: FieldScheduleEntry | null;
  onNewsClick?: (news: FieldNewsDto) => void;
}

const ScheduleEntryViewModal: React.FC<ScheduleEntryViewModalProps> = ({
  isOpen,
  onClose,
  entry,
  onNewsClick
}) => {
  const [linkedNews, setLinkedNews] = useState<FieldNewsDto | null>(null);
  const [newsLoading, setNewsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && entry && entry.entryType === 'event') {
      loadLinkedNews();
    } else {
      setLinkedNews(null);
    }
  }, [isOpen, entry?.id]);

  const loadLinkedNews = async () => {
    if (!entry) return;
    try {
      setNewsLoading(true);
      const news = await fieldNewsService.getNewsByEventId(entry.id);
      setLinkedNews(news);
    } catch (error) {
      setLinkedNews(null);
    } finally {
      setNewsLoading(false);
    }
  };

  if (!isOpen || !entry) return null;

  const color = ENTRY_TYPE_COLORS[entry.entryType];
  const typeLabel = ENTRY_TYPE_LABELS[entry.entryType];

  // Возвращаем пары {id, name} для создания ссылок
  const getReferees = (): { id: string; name: string }[] => {
    const referees: { id: string; name: string }[] = [];

    // Сначала проверяем массивы (новый формат)
    if (entry.refereeIds && entry.refereeIds.length > 0) {
      entry.refereeIds.forEach((id, index) => {
        const name = entry.refereeNames?.[index] || 'Unknown';
        referees.push({ id, name });
      });
    }
    // Fallback на одиночные поля (старый формат)
    else if (entry.refereeId) {
      referees.push({
        id: entry.refereeId,
        name: entry.refereeName || 'Unknown'
      });
    }

    return referees;
  };

  const getTitle = () => {
    if (entry.entryType === 'referee_shift') {
      return 'Referee Shift';
    }
    return entry.title || 'Event';
  };

  const referees = getReferees();

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader $color={color}>
          <ModalTitle>{getTitle()}</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          <TypeBadge $color={color}>{typeLabel}</TypeBadge>

          <InfoRow>
            <InfoLabel>Time</InfoLabel>
            <TimeDisplay>
              {formatTime(entry.startTime)} - {formatTime(entry.endTime)}
            </TimeDisplay>
          </InfoRow>

          <InfoRow>
            <InfoLabel>Date</InfoLabel>
            <InfoValue>
              {new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </InfoValue>
          </InfoRow>

          {referees.length > 0 && (
            <InfoRow>
              <InfoLabel>{referees.length === 1 ? 'Referee' : 'Referees'}</InfoLabel>
              <RefereeList>
                {referees.map((ref) => (
                  <RefereeBadge key={ref.id} to={`/referee/public/${ref.id}`}>
                    {ref.name}
                  </RefereeBadge>
                ))}
              </RefereeList>
            </InfoRow>
          )}

          {entry.entryType === 'referee_shift' && entry.gameType && (
            <InfoRow>
              <InfoLabel>Game Type</InfoLabel>
              <InfoValue style={{ textTransform: 'capitalize' }}>{entry.gameType}</InfoValue>
            </InfoRow>
          )}

          {entry.description && (
            <InfoRow>
              <InfoLabel>Description</InfoLabel>
              <Description>{entry.description}</Description>
            </InfoRow>
          )}

          {entry.entryType === 'event' && linkedNews && (
            <NewsSection>
              <NewsSectionTitle>Related News</NewsSectionTitle>
              <NewsTitle>{linkedNews.title}</NewsTitle>
              <NewsLinkButton
                onClick={() => {
                  if (onNewsClick) {
                    onNewsClick(linkedNews);
                    onClose();
                  }
                }}
              >
                Read News
              </NewsLinkButton>
            </NewsSection>
          )}
        </ModalBody>

        <ModalFooter>
          <CloseBtn onClick={onClose}>Close</CloseBtn>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default ScheduleEntryViewModal;
